import type { ComponentPropsWithoutRef, RefObject } from 'react';
import type { To } from 'react-router';
import { Menu } from '@base-ui/react/menu';
import React from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { ButtonMenuArrowProp, ButtonProps } from '../../controls/Button/Button';
import type { ShortcutKey } from '../../formatting/Shortcut/Shortcut';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { BadgeProps } from '../Badge/Badge';
import type { ToggleInputProps } from '../ToggleInput/ToggleInput';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { baseUIPopupMotionStyles } from '../../common/animations';
import { devices } from '../../common/devices';
import { selectors } from '../../common/selectors';
import { transitions } from '../../common/transitions';
import { stateZIndexes, zIndexes } from '../../common/z_indexes';
import { Button } from '../../controls/Button/Button';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import Shortcut, { shortcutsVisible } from '../../formatting/Shortcut/Shortcut';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isEmptyNode } from '../../utilities/isEmptyNode';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { VStack } from '../../utilities/Stack/VStack';
import {
  type AccessLevelProp,
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { resolveVariant } from '../../utilities/usePresentation';
import { Badge } from '../Badge/Badge';
import { BadgeGroup } from '../Badge/BadgeGroup';
import { ToggleInput } from '../ToggleInput/ToggleInput';
import { Tooltip } from '../Tooltip/Tooltip';

type MenuItemBaseProps = ComponentPropsWithoutRef<typeof Menu.Item>;
type MenuCheckboxItemBaseProps = ComponentPropsWithoutRef<typeof Menu.CheckboxItem>;
type MenuRadioItemBaseProps = ComponentPropsWithoutRef<typeof Menu.RadioItem>;
type MenuRadioGroupBaseProps = ComponentPropsWithoutRef<typeof Menu.RadioGroup>;
type MenuPopupBaseProps = ComponentPropsWithoutRef<typeof Menu.Popup>;

export const DropdownMenuTrigger = Menu.Trigger;

export const TRIGGER_DATA_ATTRIBUTE = 'data-dropdown-menu-trigger';
export const TRIGGER_SELECTOR = `[${TRIGGER_DATA_ATTRIBUTE}]`;

/**
 * Matches the hidden spans Base UI renders on either side of an open popup's trigger. They are
 * siblings of the trigger, so positional selectors around a trigger have to account for them.
 */
export const FOCUS_GUARD_SELECTOR = '[data-base-ui-focus-guard]';

/**
 * Create a special version of the `:first-child` selector for a `Menu.Trigger`
 * from BaseUI that continues to match when the menu is open and BaseUI has
 * injected focus guard elements adjacent to the trigger
 *
 * @see {@link lastChildSelectorFor}
 *
 * @param trigger - Selector for the trigger element to match
 *
 * @returns A selector list, for use as a key in a style object
 */
export const firstChildSelectorFor = <T extends string>(trigger: T) =>
  `${trigger}:first-child, ${FOCUS_GUARD_SELECTOR}:first-child + ${trigger}` as const;

/**
 * Create a special version of the `:first-child` selector for a `Menu.Trigger`
 * from BaseUI that continues to match when the menu is open and BaseUI has
 * injected focus guard elements adjacent to the trigger.
 *
 * @see {@link firstChildSelectorFor}
 *
 * @param triggger - Selector for the element to match, defaulting to stitches' parent reference
 *
 * @returns A selector list, for use as a key in a style object
 */
export const lastChildSelectorFor = <T extends string>(trigger: T) =>
  `${trigger}:last-child, ${trigger}:has(+ ${FOCUS_GUARD_SELECTOR}:last-child)` as const;

/**
 * Selector to use for `Menu.Trigger` elements in place of `&:first-chlid`
 * to work around BaseUI's focus guard element injection
 */
export const firstChildSelector = firstChildSelectorFor('&');

/**
 * Selector to use for `Menu.Trigger` elements in place of `&:last-chlid`
 * to work around BaseUI's focus guard element injection
 */
export const lastChildSelector = lastChildSelectorFor('&');

export function DropdownMenuButton({
  menuArrow = 'dropdown',
  size,
  variant = 'secondary',
  ref,
  disabled,
  truncate = false,
  ...remaining
}: Omit<ButtonProps, 'menuArrow'> & {
  ref?: React.Ref<HTMLButtonElement | null>;
  menuArrow?: ButtonMenuArrowProp | null;
}) {
  return (
    <Menu.Trigger
      disabled={disabled}
      render={(props) => (
        <Button
          {...remaining}
          {...props}
          {...{ [TRIGGER_DATA_ATTRIBUTE]: '' }}
          ref={mergeRefs([ref, props.ref].filter(Boolean) as React.Ref<HTMLButtonElement | null>[])}
          disabled={disabled}
          labelStretch
          menuArrow={menuArrow ?? undefined}
          size={size}
          truncate={truncate}
          variant={variant}
        />
      )}
    />
  );
}

const DropdownMenuItemGlyph = styled(DynamicIcon, {
  color: '$$iconColor',

  variants: {
    isCheckbox: {
      true: {
        [devices.touch]: {
          width: '$14',
          height: '$14',
        },

        [devices.mouse]: {
          width: '$10',
          height: '$10',
        },
      },
      false: {
        [devices.touch]: {
          width: '$18',
          height: '$18',
        },

        [devices.mouse]: {
          width: '$14',
          height: '$14',
        },
      },
    },
    internal: {
      true: {
        color: '$$internalIconColor',
      },
      false: {},
    },
  },
});
DropdownMenuItemGlyph.displayName = 'DropdownMenuItemGlyph';

const DropdownMenuItemIcon = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '$4',

  [devices.touch]: {
    size: '$18',
  },

  [devices.mouse]: {
    size: '$14',
  },

  variants: {
    checked: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    isCheckbox: {
      true: {},
      false: {},
    },
    isRadio: {
      true: {
        borderRadius: '99rem',
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      isCheckbox: true,
      checked: true,
      css: {
        background: colors.tokenBgBrandLight,

        [darkThemeSelector]: {
          background: colors.tokenBgBrandDark,
        },
      },
    },
    {
      isCheckbox: true,
      checked: false,
      css: {
        background: colors.tokenBgNeutralLight,

        [darkThemeSelector]: {
          background: colors.tokenBgNeutralDark,
        },
      },
    },
    {
      isCheckbox: true,
      checked: true,
      internal: true,
      css: {
        background: colors.internalTokenBgLight,

        [darkThemeSelector]: {
          background: colors.internalTokenBgDark,
        },
      },
    },
    {
      isRadio: true,
      checked: true,
      css: {
        background: colors.tokenBgBrandLight,

        [darkThemeSelector]: {
          background: colors.tokenBgBrandDark,
        },
      },
    },
    {
      isRadio: true,
      checked: false,
      css: {
        background: colors.tokenBgNeutralLight,

        [darkThemeSelector]: {
          background: colors.tokenBgNeutralDark,
        },
      },
    },
    {
      isRadio: true,
      checked: true,
      internal: true,
      css: {
        background: colors.internalTokenBgLight,

        [darkThemeSelector]: {
          background: colors.internalTokenBgDark,
        },
      },
    },
  ],
});
DropdownMenuItemIcon.displayName = 'DropdownMenuItemIcon';

const DropdownMenuItemRadioDot = styled('div', {
  borderRadius: '99rem',
  background: '$$iconColor',

  [devices.touch]: {
    size: '$8',
  },

  [devices.mouse]: {
    size: '$6',
  },

  variants: {
    internal: {
      true: {
        background: '$$internalIconColor',
      },
      false: {},
    },
  },
});
DropdownMenuItemRadioDot.displayName = 'DropdownMenuItemRadioDot';

const DropdownMenuItemLabel = styled(Text, {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  color: '$$labelColor',

  [devices.touch]: {
    gap: '$8',
  },

  [devices.mouse]: {
    gap: '$6',
  },

  variants: {
    internal: {
      true: {
        color: '$$internalLabelColor',
      },
      false: {},
    },
  },
});
DropdownMenuItemLabel.displayName = 'DropdownMenuItemLabel';

const DropdownMenuItemDescription = styled(Small, {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  color: '$$labelColor',

  variants: {
    internal: {
      true: {
        color: '$$internalLabelColor',
      },
      false: {},
    },
  },
});
DropdownMenuItemDescription.displayName = 'DropdownMenuItemDescription';

const dropdownMenuItemContainer = {
  position: 'relative',
  zIndex: stateZIndexes.base,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  background: '$$backgroundColor',
  cursor: 'pointer',
  transition: transitions.focusRing,
  $$iconColor: colors.iconNeutralLight,
  $$labelColor: colors.bodyNeutralLight,
  $$descriptionColor: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    $$iconColor: colors.iconNeutralDark,
    $$labelColor: colors.bodyNeutralDark,
    $$descriptionColor: colors.bodyNeutralDark,
  },

  [devices.touch]: {
    gap: '$8',
    padding: '$6 $10',
    borderRadius: '$10',
  },

  [devices.mouse]: {
    gap: '$6',
    padding: '$4 $8',
    borderRadius: '$8',
  },

  [selectors.focus]: {
    zIndex: stateZIndexes.focus,
    outline: 'none',
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  '&[aria-disabled="true"]': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  '&[aria-checked="true"]': {
    zIndex: stateZIndexes.selected,
    outline: 'none',
    $$iconColor: colors.iconBrandLight,
    $$labelColor: colors.bodyBrandLight,
    $$descriptionColor: colors.bodyBrandLight,

    [darkThemeSelector]: {
      $$iconColor: colors.iconBrandDark,
      $$labelColor: colors.bodyBrandDark,
      $$descriptionColor: colors.bodyBrandDark,
    },

    '&[data-highlighted]': {
      $$iconColor: colors.bodyBrandLight,
      $$labelColor: colors.headingBrandLight,
      $$descriptionColor: colors.bodyBrandLight,

      [darkThemeSelector]: {
        $$iconColor: colors.bodyBrandDark,
        $$labelColor: colors.headingBrandDark,
        $$descriptionColor: colors.bodyBrandDark,
      },
    },
  },

  '&[data-highlighted]': {
    zIndex: stateZIndexes.focus,
    outline: 'none',
    background: colors.bgNeutralLight,
    $$iconColor: colors.bodyNeutralLight,
    $$labelColor: colors.headingNeutralLight,
    $$descriptionColor: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      background: colors.bgNeutralDark,
      $$iconColor: colors.bodyNeutralDark,
      $$labelColor: colors.headingNeutralDark,
      $$descriptionColor: colors.bodyNeutralDark,
    },
  },

  // Focus ring only for keyboard navigation (not mouse hover)
  '&[data-highlighted]:not(:hover)': {
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    fill: {
      true: {},
      false: {},
    },
    selected: {
      true: {
        zIndex: stateZIndexes.selected,
        $$backgroundColor: colors.bgBrandLight,
        $$iconColor: colors.iconBrandLight,
        $$labelColor: colors.bodyBrandLight,
        $$descriptionColor: colors.bodyBrandLight,

        [darkThemeSelector]: {
          $$backgroundColor: colors.bgBrandDark,
          $$iconColor: colors.iconBrandDark,
          $$labelColor: colors.bodyBrandDark,
          $$descriptionColor: colors.bodyBrandDark,
        },

        '&[data-highlighted]': {
          background: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$iconColor: colors.bodyBrandLight,
          $$labelColor: colors.headingBrandLight,
          $$descriptionColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$iconColor: colors.bodyBrandDark,
            $$labelColor: colors.headingBrandDark,
            $$descriptionColor: colors.bodyBrandDark,
          },
        },
      },
      false: {},
    },
    variant: {
      neutral: {},
      negative: {
        $$iconColor: colors.iconNegativeLight,
        $$labelColor: colors.bodyNegativeLight,
        $$descriptionColor: colors.bodyNegativeLight,

        [darkThemeSelector]: {
          $$iconColor: colors.iconNegativeDark,
          $$labelColor: colors.bodyNegativeDark,
          $$descriptionColor: colors.bodyNegativeDark,
        },

        '&[aria-checked="true"]': {
          $$iconColor: colors.iconNegativeLight,
          $$labelColor: colors.bodyNegativeLight,
          $$descriptionColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            $$iconColor: colors.iconNegativeDark,
            $$labelColor: colors.bodyNegativeDark,
            $$descriptionColor: colors.bodyNegativeDark,
          },

          '&[data-highlighted]': {
            background: colors.bgNegativeLight,
            strokeAll: colors.strokeNegativeLight,
            $$iconColor: colors.bodyNegativeLight,
            $$labelColor: colors.headingNegativeLight,
            $$descriptionColor: colors.bodyNegativeLight,

            [darkThemeSelector]: {
              background: colors.bgNegativeDark,
              strokeAll: colors.strokeNegativeDark,
              $$iconColor: colors.bodyNegativeDark,
              $$labelColor: colors.headingNegativeDark,
              $$descriptionColor: colors.bodyNegativeDark,
            },
          },
        },
      },
    },
    internal: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      fill: true,
      css: {
        '&[aria-checked="true"]': {
          background: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
          },
        },
      },
    },
    {
      selected: true,
      fill: true,
      css: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
    },
    {
      variant: 'negative',
      fill: true,
      css: {
        '&[aria-checked="true"]': {
          background: colors.bgNegativeLight,
          strokeAll: colors.strokeNegativeLight,

          [darkThemeSelector]: {
            background: colors.bgNegativeDark,
            strokeAll: colors.strokeNegativeDark,
          },
        },
      },
    },
    {
      internal: true,
      css: {
        $$internalIconColor: colors.internalIconLight,
        $$internalLabelColor: colors.internalBodyLight,
        $$internalDescriptionColor: colors.internalBodyLight,

        [darkThemeSelector]: {
          $$internalIconColor: colors.internalIconDark,
          $$internalLabelColor: colors.internalBodyDark,
          $$internalDescriptionColor: colors.internalBodyDark,
        },

        '&[aria-checked="true"]': {
          $$internalIconColor: colors.internalBodyLight,
          $$internalLabelColor: colors.internalHeadingLight,
          $$internalDescriptionColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            $$internalIconColor: colors.internalBodyDark,
            $$internalLabelColor: colors.internalHeadingDark,
            $$internalDescriptionColor: colors.internalBodyDark,
          },
        },

        '&[data-highlighted]': {
          $$internalIconColor: colors.internalBodyLight,
          $$internalLabelColor: colors.internalHeadingLight,
          $$internalDescriptionColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            $$internalIconColor: colors.internalBodyDark,
            $$internalLabelColor: colors.internalHeadingDark,
            $$internalDescriptionColor: colors.internalBodyDark,
          },
        },
      },
    },
    {
      internal: true,
      fill: true,
      css: {
        '&[aria-checked="true"]': {
          background: colors.internalBgLight,
          strokeAll: colors.internalStrokeLight,

          [darkThemeSelector]: {
            background: colors.internalBgDark,
            strokeAll: colors.internalStrokeDark,
          },
        },
      },
    },
  ],
};

type DropdownMenuItemInnerPropsWithoutRouting = {
  accessLevel?: AccessLevelProp | null;
  badge?: BadgeProps;
  description?: React.ReactNode;
  endAdornment?: React.ReactNode;
  icon?: DynamicIconName;
  internal?: boolean;
  isCheckbox?: boolean;
  isRadio?: boolean;
  label?: React.ReactNode;
  selected?: boolean;
  tooltip?: React.ReactNode;
  shortcut?: ShortcutKey[];
  variant?: 'neutral' | 'negative';
};

type DropdownMenuItemInnerProps =
  | (DropdownMenuItemInnerPropsWithoutRouting & { as?: undefined; to?: undefined })
  | (DropdownMenuItemInnerPropsWithoutRouting & { as: React.ElementType; to?: To });

function DropdownMenuItemInner({
  accessLevel: accessLevelProp,
  badge,
  description,
  endAdornment,
  icon,
  internal: internalProp,
  isCheckbox,
  isRadio,
  label,
  tooltip,
  selected,
  shortcut,
  variant,
  as,
  to,
}: DropdownMenuItemInnerProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  const hasStart = Boolean(icon || isCheckbox || isRadio);
  const hasBadges = Boolean((accessLevel && isAccessLevelVisible(accessLevel)) || badge);
  const hasEnd = Boolean(hasBadges || (shortcut && shortcutsVisible) || endAdornment);

  const labelContents = (
    <DropdownMenuItemLabel internal={internal} context="body" variant={variant} weight="bold">
      {label}
    </DropdownMenuItemLabel>
  );

  const contents = (
    <AlignStack
      as={as}
      to={to}
      gap={6}
      preset="body"
      start={
        hasStart && (
          <DropdownMenuItemIcon
            isCheckbox={isCheckbox}
            isRadio={isRadio}
            checked={Boolean(selected)}
            internal={internal}
          >
            {isRadio && selected && <DropdownMenuItemRadioDot internal={internal} />}
            {!isRadio && (icon || selected) && (
              <DropdownMenuItemGlyph
                isCheckbox={isCheckbox}
                internal={internal}
                icon={icon ?? 'placeholder'}
                priority={['framed']}
                variant={variant}
              />
            )}
          </DropdownMenuItemIcon>
        )
      }
      end={
        hasEnd && (
          <>
            {hasBadges && (
              <BadgeGroup relation="separate" size="small">
                {accessLevel && isAccessLevelVisible(accessLevel) && (
                  <FeatureBadge type={accessLevel} />
                )}
                {badge && (
                  <Badge
                    {...badge}
                    internal={internal}
                    variant={resolveVariant({ variant, selected })}
                  />
                )}
              </BadgeGroup>
            )}
            {shortcut && (
              <Shortcut
                internal={internal}
                keys={shortcut}
                variant={variant === 'negative' ? 'destructive' : undefined}
              />
            )}
            {endAdornment}
          </>
        )
      }
    >
      <VStack width="full">
        {label && <Text>{labelContents}</Text>}
        {description && (
          <DropdownMenuItemDescription internal={internal} variant={variant}>
            {description}
          </DropdownMenuItemDescription>
        )}
      </VStack>
    </AlignStack>
  );

  return tooltip ? <Tooltip contents={tooltip}>{contents}</Tooltip> : contents;
}

type DropdownMenuItemHrefProps =
  | { href?: undefined; target?: undefined; rel?: undefined }
  | {
      /** Renders the whole item as an anchor. Use `as`/`to` instead for in-app routes. */
      href: string;
      to?: undefined;
      target?: React.HTMLAttributeAnchorTarget;
      rel?: string;
    };

export type DropdownMenuItemProps = Omit<MenuItemBaseProps, 'onClick' | 'label' | 'children'> &
  DropdownMenuItemInnerProps &
  DropdownMenuItemHrefProps & {
    ref?: React.Ref<HTMLDivElement>;
    children?: React.ReactNode;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    typeaheadLabel?: string;
  };

const DropdownMenuItemTarget = styled('div', {
  width: '100%',
});
DropdownMenuItemTarget.displayName = 'DropdownMenuItemTarget';

const DropdownMenuItemContainer = styled(Menu.Item, {
  ...dropdownMenuItemContainer,
});
DropdownMenuItemContainer.displayName = 'DropdownMenuItemContainer';

export function DropdownMenuItem({
  accessLevel: accessLevelProp,
  badge,
  children,
  description,
  icon,
  internal: internalProp,
  label,
  ref,
  selected,
  shortcut,
  tooltip,
  variant,
  //
  as,
  to,
  href,
  target,
  rel,
  disabled,
  onClick,
  typeaheadLabel,
  ...remaining
}: DropdownMenuItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  return (
    <DropdownMenuItemContainer
      {...remaining}
      label={typeaheadLabel}
      internal={internal}
      disabled={disabled}
      fill
      selected={selected}
      nativeButton={false}
      render={(props) => (
        <DropdownMenuItemTarget
          {...props}
          ref={mergeRefs([ref, props.ref].filter(Boolean) as React.Ref<HTMLDivElement | null>[])}
          as={as || (href ? 'a' : 'div')}
          to={to}
          href={href}
          target={target}
          rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            props.onClick?.(e);
            onClick?.(e);
          }}
        >
          <DropdownMenuItemInner
            accessLevel={accessLevelProp}
            badge={badge}
            description={description}
            icon={icon}
            internal={internalProp}
            label={label || children}
            selected={selected}
            shortcut={shortcut}
            variant={variant}
            tooltip={tooltip}
          />
        </DropdownMenuItemTarget>
      )}
    />
  );
}

const DropdownMenuCheckboxItemContainer = styled(Menu.CheckboxItem, {
  ...dropdownMenuItemContainer,
});
DropdownMenuCheckboxItemContainer.displayName = 'DropdownMenuCheckboxItemContainer';

export type DropdownCheckboxItemProps = Omit<
  MenuCheckboxItemBaseProps,
  'onClick' | 'label' | 'onSelect'
> &
  DropdownMenuItemInnerProps & {
    ref?: RefObject<HTMLDivElement | null>;
    hideCheckmark?: boolean;
    onClick?: MenuCheckboxItemBaseProps['onClick'];
    /** @ignore Radix API; unused with Base UI. */
    textValue?: string;
  };

export function DropdownMenuCheckboxItem({
  accessLevel: accessLevelProp,
  checked,
  children,
  badge,
  description,
  hideCheckmark,
  icon,
  internal: internalProp,
  label,
  ref,
  shortcut,
  tooltip,
  onClick,
  textValue: _textValue,
  ...remaining
}: DropdownCheckboxItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  const handleClick = onClick
    ? (event: Parameters<NonNullable<MenuCheckboxItemBaseProps['onClick']>>[0]) => {
        onClick(event);
        // Prevent Base UI's default checkbox toggle when using a custom onClick handler.
        event.preventBaseUIHandler?.();
      }
    : undefined;

  return (
    <DropdownMenuCheckboxItemContainer
      {...remaining}
      checked={checked}
      fill={false}
      internal={internal}
      ref={ref}
      onClick={handleClick}
    >
      <DropdownMenuItemInner
        isCheckbox={!hideCheckmark}
        accessLevel={accessLevelProp}
        badge={badge}
        description={description}
        icon={checked ? 'checkmark' : icon}
        selected={Boolean(checked)}
        internal={internalProp}
        label={label || children}
        shortcut={shortcut}
        tooltip={tooltip}
      />
    </DropdownMenuCheckboxItemContainer>
  );
}

export type DropdownMenuToggleItemProps = Omit<
  MenuCheckboxItemBaseProps,
  'onClick' | 'label' | 'onSelect'
> &
  Omit<DropdownMenuItemInnerProps, 'selected'> & {
    ref?: RefObject<HTMLDivElement | null>;
    /**
     * When true (default), the menu stays open when the toggle is clicked.
     * When false, the menu closes after toggle.
     */
    keepOpenOnToggle?: boolean;
    /** Props passed to the ToggleInput (variant, size, etc.) */
    toggleProps?: Pick<ToggleInputProps, 'variant' | 'size'>;
    onClick?: MenuCheckboxItemBaseProps['onClick'];
  };

const DropdownMenuToggleItemWrapper = styled('div', {
  display: 'flex',
  paddingLeft: '$4',
  pointerEvents: 'none',
});

export function DropdownMenuToggleItem({
  accessLevel: accessLevelProp,
  checked,
  children,
  badge,
  description,
  icon,
  internal: internalProp,
  keepOpenOnToggle = true,
  label,
  onCheckedChange,
  ref,
  shortcut,
  tooltip,
  toggleProps,
  variant,
  onClick,
  disabled,
  ...remaining
}: DropdownMenuToggleItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  return (
    <DropdownMenuCheckboxItemContainer
      {...remaining}
      checked={checked}
      closeOnClick={!keepOpenOnToggle}
      fill
      internal={internal}
      ref={ref}
      onCheckedChange={onCheckedChange}
      onClick={onClick}
      disabled={disabled}
    >
      <DropdownMenuItemInner
        accessLevel={accessLevelProp}
        badge={badge}
        description={description}
        endAdornment={
          <DropdownMenuToggleItemWrapper>
            <ToggleInput
              size="small"
              selected={Boolean(checked)}
              {...toggleProps}
              disabled={disabled}
            />
          </DropdownMenuToggleItemWrapper>
        }
        icon={icon}
        internal={internalProp}
        label={label || children}
        shortcut={shortcut}
        tooltip={tooltip}
        variant={variant}
      />
    </DropdownMenuCheckboxItemContainer>
  );
}

const DropdownMenuRadioItemContainer = styled(Menu.RadioItem, {
  ...dropdownMenuItemContainer,
});
DropdownMenuRadioItemContainer.displayName = 'DropdownMenuRadioItemContainer';

export type DropdownRadioItemProps = Omit<
  MenuRadioItemBaseProps,
  'onClick' | 'label' | 'onSelect'
> &
  DropdownMenuItemInnerProps & {
    ref?: RefObject<HTMLDivElement | null>;
    onClick?: MenuRadioItemBaseProps['onClick'];
    /** Show a radio mark instead of filling the selected item. */
    showRadio?: boolean;
  };

export function DropdownMenuRadioItem({
  accessLevel: accessLevelProp,
  badge,
  children,
  description,
  icon,
  internal: internalProp,
  label,
  ref,
  shortcut,
  showRadio = false,
  tooltip,
  variant,
  onClick,
  ...remaining
}: DropdownRadioItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  if (accessLevel === null) return null;

  return (
    <DropdownMenuRadioItemContainer
      {...remaining}
      fill={!showRadio}
      internal={internal}
      ref={ref}
      onClick={onClick}
      render={(props: React.ComponentProps<'div'>, state: Menu.RadioItem.State) => (
        <div {...props}>
          <DropdownMenuItemInner
            accessLevel={accessLevelProp}
            badge={badge}
            description={description}
            icon={icon}
            internal={internalProp}
            isRadio={showRadio}
            label={label || children}
            selected={showRadio && state.checked}
            shortcut={shortcut}
            tooltip={tooltip}
            variant={variant}
          />
        </div>
      )}
    />
  );
}

const dropdownMenuLabelStyles = {
  color: colors.controlContentPlaceholderLight,

  [devices.touch]: {
    padding: '$6 $10',
  },

  [devices.mouse]: {
    padding: '$4 $8',
  },

  [darkThemeSelector]: {
    color: colors.controlContentPlaceholderDark,
  },
};

// Menu.GroupLabel requires a Menu.Group or Menu.RadioGroup context
const DropdownMenuLabel = styled(Menu.GroupLabel, Small, dropdownMenuLabelStyles);
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const dropdownMenuGroupContainer = {
  display: 'flex',
  flexDirection: 'column',

  [devices.touch]: {
    padding: '$8',
  },

  [devices.mouse]: {
    padding: '$6',
  },

  '&:not(:last-child)': {
    strokeBottom: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      strokeBottom: colors.strokeNeutralDark,
    },
  },
};

const dropdownMenuGroupVariants = {
  scrollable: {
    true: {
      overflow: 'auto',
      flex: '1 1 auto',
      minHeight: 0,
    },
    false: {
      flex: '0 0 auto',
    },
  },
  isNested: {
    true: {
      [devices.touch]: {
        padding: '$8 0',
      },

      [devices.mouse]: {
        padding: '$6 0',
      },
    },
  },
};

const DropdownMenuGroupContainer = styled(Menu.Group, {
  ...dropdownMenuGroupContainer,
  variants: dropdownMenuGroupVariants,
});
DropdownMenuGroupContainer.displayName = 'DropdownMenuGroupContainer';

type DropdownMenuGroupProps = {
  children: React.ReactNode;
  label?: React.ReactNode;
};

export function DropdownMenuGroup({
  children,
  isNested = false,
  label,
  scrollable = false,
  ...remaining
}: {
  children: React.ReactNode;
  isNested?: boolean;
  label?: React.ReactNode;
  scrollable?: boolean;
}) {
  // Don't render empty groups
  if (isEmptyNode(children)) {
    return null;
  }

  return (
    <DropdownMenuGroupContainer
      isNested={isNested}
      scrollable={scrollable}
      data-scrollable={scrollable ? 'true' : undefined}
      {...remaining}
    >
      {label && <DropdownMenuLabel>{label}</DropdownMenuLabel>}
      {children}
    </DropdownMenuGroupContainer>
  );
}

const DropdownMenuRadioGroupContainer = styled(Menu.RadioGroup, {
  ...dropdownMenuGroupContainer,
  variants: dropdownMenuGroupVariants,
});
DropdownMenuRadioGroupContainer.displayName = 'DropdownMenuRadioGroupContainer';

export function DropdownMenuRadioGroup({
  children,
  isNested = false,
  label,
  scrollable = false,
  ...remaining
}: MenuRadioGroupBaseProps &
  DropdownMenuGroupProps & {
    isNested?: boolean;
    scrollable?: boolean;
  }) {
  return (
    <DropdownMenuRadioGroupContainer
      isNested={isNested}
      scrollable={scrollable}
      data-scrollable={scrollable ? 'true' : undefined}
      {...remaining}
    >
      {label && <DropdownMenuLabel>{label}</DropdownMenuLabel>}
      {children}
    </DropdownMenuRadioGroupContainer>
  );
}

const DropdownMenuPositioner = styled(Menu.Positioner, {
  zIndex: zIndexes.overlay,

  variants: {
    belowDialog: {
      true: {
        zIndex: zIndexes.globalOverlay,
      },
      false: {},
    },
  },
});
DropdownMenuPositioner.displayName = 'DropdownMenuPositioner';

const DropdownMenuPopoverContainer = styled(Menu.Popup, baseUIPopupMotionStyles, {
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$10',

  minWidth: '$180',
  maxWidth: 'min($400, var(--available-width))',
  maxHeight: 'var(--available-height)',

  '@mobile': {
    width: 'var(--available-width)',
  },

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  variants: {
    hasInnerScroll: {
      true: {},
      false: {
        overflow: 'auto',
      },
    },
    matchTrigger: {
      true: {
        minWidth: 'var(--anchor-width)',
      },
      false: {},
    },
  },
});
DropdownMenuPopoverContainer.displayName = 'DropdownMenuPopoverContainer';

export type DropdownMenuPopoverProps = Omit<
  MenuPopupBaseProps,
  'children' | 'defaultValue' | 'defaultChecked'
> &
  Pick<
    ComponentPropsWithoutRef<typeof Menu.Positioner>,
    'align' | 'alignOffset' | 'collisionAvoidance' | 'collisionPadding' | 'side' | 'sideOffset'
  > & {
    children: React.ReactNode;
    container?: HTMLElement | null | undefined;
    /**
     * If true, will allow the inner content to scroll when inside a scrollable group.
     * @default false
     */
    hasInnerScroll?: boolean;
    /**
     * If true, will match the width of the trigger.
     * @default false
     */
    matchTrigger?: boolean;
    /**
     * If false, will render the contents in place instead of portalling to `document.body`.
     *
     * @default true
     */
    usePortal?: boolean;
    /**
     * If set, will render the popover below any dialogs. For global or application-wide controls.
     */
    belowDialog?: boolean;
    width?: SpacingScale;
    maxWidth?: SpacingScale;
    ref?: React.Ref<HTMLDivElement | null>;
  };

export function DropdownMenuPopover({
  align = 'end',
  alignOffset,
  children,
  collisionAvoidance,
  container,
  collisionPadding = 12,
  hasInnerScroll = false,
  matchTrigger = false,
  side,
  sideOffset = 4,
  usePortal = true,
  width,
  maxWidth,
  belowDialog = false,
  ref: forwardedRef,
  ...popupProps
}: DropdownMenuPopoverProps) {
  const css = width || maxWidth ? { ...widthCSS(width), ...maxWidthCSS(maxWidth) } : undefined;

  const positioner = (
    <DropdownMenuPositioner
      align={align || 'end'}
      alignOffset={alignOffset}
      collisionAvoidance={collisionAvoidance}
      collisionPadding={collisionPadding}
      belowDialog={belowDialog}
      side={side}
      sideOffset={sideOffset}
    >
      <DropdownMenuPopoverContainer
        hasInnerScroll={hasInnerScroll}
        matchTrigger={matchTrigger}
        css={css}
        ref={forwardedRef}
        {...popupProps}
      >
        {children}
      </DropdownMenuPopoverContainer>
    </DropdownMenuPositioner>
  );

  return usePortal ? <Menu.Portal container={container}>{positioner}</Menu.Portal> : positioner;
}

export const DropdownMenu = Menu.Root;
